import { useState } from 'react';
import { TrendingDown, AlertTriangle, X } from 'lucide-react';
import { useApi } from '@/hooks/useApi';

// ─── Types ────────────────────────────────────────────────────

interface AttritionSignals {
  tenure_score: number;
  review_trajectory: number;
  pto_hoarding_score: number;
  manager_change_score: number;
  promotion_cadence_score: number;
  salary_lag_score: number;
  oncall_fatigue_score: number;
}

interface AttritionScore {
  employee_id: string;
  employee_name: string;
  title: string;
  department: string;
  risk_score: number;
  risk_tier: 'low' | 'medium' | 'high' | 'critical';
  signals: AttritionSignals;
  computed_at: string;
}

interface AttritionScoreList {
  scores: AttritionScore[];
  total: number;
}

interface TrendWeek {
  week: string;
  pct_at_risk: number;
  pct_critical: number;
  pct_high: number;
  pct_medium: number;
  pct_low: number;
}

interface AttritionTrends {
  weeks: TrendWeek[];
  current_distribution: {
    low: number;
    medium: number;
    high: number;
    critical: number;
    total: number;
    pct_at_risk: number;
  };
}

// ─── Components ───────────────────────────────────────────────

const TIER_STYLES: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400 border border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  low: 'bg-green-500/20 text-green-400 border border-green-500/30',
};

const TIER_COLORS: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-500',
  low: 'bg-green-500',
};

function TierBadge({ tier }: { tier: string }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${TIER_STYLES[tier] ?? TIER_STYLES['low']}`}>
      {tier}
    </span>
  );
}

// Donut SVG for risk distribution
function RiskDonut({ dist }: { dist: AttritionTrends['current_distribution'] }) {
  const total = dist.total || 1;
  const segments = [
    { key: 'critical', count: dist.critical, color: '#ef4444' },
    { key: 'high', count: dist.high, color: '#f97316' },
    { key: 'medium', count: dist.medium, color: '#f59e0b' },
    { key: 'low', count: dist.low, color: '#22c55e' },
  ];

  const r = 60;
  const cx = 80;
  const cy = 80;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  const paths = segments.map(seg => {
    const pct = seg.count / total;
    const dash = pct * circumference;
    const path = (
      <circle
        key={seg.key}
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={seg.color}
        strokeWidth="20"
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${cx} ${cy})`}
        opacity={seg.count === 0 ? 0.1 : 0.85}
      />
    );
    offset += dash;
    return path;
  });

  return (
    <div className="flex items-center gap-6">
      <svg width="160" height="160">
        {/* Background circle */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#27272a" strokeWidth="20" />
        {paths}
        <text x={cx} y={cy - 8} textAnchor="middle" className="fill-foreground" style={{ fontSize: 22, fontWeight: 700, fill: '#f4f4f5' }}>
          {total}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" style={{ fontSize: 11, fill: '#71717a' }}>
          employees
        </text>
      </svg>
      <div className="space-y-2">
        {segments.map(seg => (
          <div key={seg.key} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="capitalize text-foreground w-16">{seg.key}</span>
            <span className="font-semibold text-foreground">{seg.count}</span>
            <span className="text-muted-foreground text-xs">({Math.round((seg.count / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 12-week trend line SVG
function TrendChart({ weeks }: { weeks: TrendWeek[] }) {
  if (weeks.length === 0) return null;

  const width = 400;
  const height = 120;
  const pad = { top: 10, right: 10, bottom: 24, left: 32 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;

  const maxPct = Math.max(...weeks.map(w => w.pct_at_risk), 10);
  const xStep = chartW / (weeks.length - 1);

  const points = weeks.map((w, i) => ({
    x: pad.left + i * xStep,
    y: pad.top + chartH - (w.pct_at_risk / maxPct) * chartH,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const fillD = `${pathD} L${points[points.length - 1]!.x},${pad.top + chartH} L${points[0]!.x},${pad.top + chartH} Z`;

  return (
    <div>
      <h4 className="text-xs font-medium text-muted-foreground mb-2">% At Risk (12-week trend)</h4>
      <svg width={width} height={height} className="w-full">
        {/* Y axis labels */}
        {[0, maxPct / 2, maxPct].map((v, i) => (
          <text key={i} x={pad.left - 4} y={pad.top + chartH - (v / maxPct) * chartH + 4} textAnchor="end" style={{ fontSize: 9, fill: '#71717a' }}>
            {Math.round(v)}%
          </text>
        ))}

        {/* Horizontal gridlines */}
        {[0, 0.5, 1].map((frac, i) => (
          <line
            key={i}
            x1={pad.left}
            x2={pad.left + chartW}
            y1={pad.top + chartH - frac * chartH}
            y2={pad.top + chartH - frac * chartH}
            stroke="#27272a"
            strokeWidth="1"
          />
        ))}

        {/* Fill area */}
        <path d={fillD} fill="#f97316" opacity="0.1" />

        {/* Line */}
        <path d={pathD} fill="none" stroke="#f97316" strokeWidth="2" strokeLinejoin="round" />

        {/* Data points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill="#f97316" />
        ))}

        {/* X axis — every 4 weeks */}
        {weeks.map((w, i) => i % 4 === 0 && (
          <text key={i} x={pad.left + i * xStep} y={height - 4} textAnchor="middle" style={{ fontSize: 9, fill: '#71717a' }}>
            {w.week.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

// Signal breakdown modal
function SignalModal({ score, onClose }: { score: AttritionScore; onClose: () => void }) {
  const SIGNAL_LABELS: Record<keyof AttritionSignals, string> = {
    tenure_score: 'Tenure Risk',
    review_trajectory: 'Review Trajectory',
    pto_hoarding_score: 'PTO Hoarding',
    manager_change_score: 'Manager Changes',
    promotion_cadence_score: 'Promotion Cadence',
    salary_lag_score: 'Salary Lag',
    oncall_fatigue_score: 'On-Call Fatigue',
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-1 border border-border rounded-2xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground">{score.employee_name}</h3>
            <p className="text-xs text-muted-foreground">{score.title} · {score.department}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <TierBadge tier={score.risk_tier} />
          <span className="text-2xl font-bold text-foreground">{score.risk_score}</span>
          <span className="text-muted-foreground text-sm">/100 risk score</span>
        </div>

        <div className="space-y-3">
          {(Object.entries(score.signals) as [keyof AttritionSignals, number][]).map(([key, val]) => (
            <div key={key}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">{SIGNAL_LABELS[key]}</span>
                <span className="font-medium text-foreground">{val}</span>
              </div>
              <div className="h-2 bg-surface-3 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${val >= 60 ? 'bg-red-500' : val >= 40 ? 'bg-amber-500' : 'bg-green-500'}`}
                  style={{ width: `${val}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">Computed {new Date(score.computed_at).toLocaleDateString()}</p>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function AttritionModelPage() {
  const [filterTier, setFilterTier] = useState('all');
  const [selectedScore, setSelectedScore] = useState<AttritionScore | null>(null);

  const { data: scoresData, loading: scoresLoading } = useApi<AttritionScoreList>(
    filterTier !== 'all' ? `people/attrition/scores?risk_tier=${filterTier}` : 'people/attrition/scores',
    [filterTier],
  );
  const { data: trendsData, loading: trendsLoading } = useApi<AttritionTrends>('people/attrition/trends');

  const loading = scoresLoading || trendsLoading;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
          <TrendingDown className="w-5 h-5 text-orange-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">Predictive Attrition Model</h1>
          <p className="text-sm text-muted-foreground">7-signal risk scoring to identify flight risks before they leave</p>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading attrition data…</div>
      ) : (
        <>
          {/* Distribution + Trend */}
          <div className="grid lg:grid-cols-2 gap-6">
            {trendsData && (
              <div className="glass rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Risk Distribution</h3>
                <RiskDonut dist={trendsData.current_distribution} />
                <div className="pt-2 border-t border-border/50">
                  <p className="text-xs text-muted-foreground">
                    <span className="text-foreground font-medium">{trendsData.current_distribution.pct_at_risk}%</span> of employees are high or critical risk
                  </p>
                </div>
              </div>
            )}
            {trendsData && (
              <div className="glass rounded-xl p-5">
                <TrendChart weeks={trendsData.weeks} />
              </div>
            )}
          </div>

          {/* Employee Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                Risk Scores ({scoresData?.total ?? 0} employees)
              </h3>
              <div className="flex gap-1">
                {(['all', 'critical', 'high', 'medium', 'low'] as const).map(tier => (
                  <button
                    key={tier}
                    onClick={() => setFilterTier(tier)}
                    className={`px-2.5 py-1 rounded text-xs capitalize transition-colors ${filterTier === tier ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'}`}
                  >
                    {tier}
                  </button>
                ))}
              </div>
            </div>

            {scoresData?.scores.length === 0 ? (
              <div className="glass rounded-xl p-8 text-center">
                <AlertTriangle className="w-10 h-10 text-amber-500/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  No attrition scores computed yet. The weekly timer runs every Monday at 6 AM UTC.
                </p>
              </div>
            ) : (
              <div className="glass rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Employee</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Department</th>
                      <th className="text-right p-3 text-xs font-medium text-muted-foreground">Risk Score</th>
                      <th className="text-right p-3 text-xs font-medium text-muted-foreground">Tier</th>
                      <th className="text-right p-3 text-xs font-medium text-muted-foreground">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoresData?.scores.map(score => (
                      <tr key={score.employee_id} className="border-b border-border/50 hover:bg-surface-3/30">
                        <td className="p-3">
                          <p className="font-medium text-foreground">{score.employee_name}</p>
                          <p className="text-xs text-muted-foreground">{score.title}</p>
                        </td>
                        <td className="p-3 text-muted-foreground">{score.department}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${TIER_COLORS[score.risk_tier]}`}
                                style={{ width: `${score.risk_score}%` }}
                              />
                            </div>
                            <span className="font-bold text-foreground w-8 text-right">{score.risk_score}</span>
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <TierBadge tier={score.risk_tier} />
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => setSelectedScore(score)}
                            className="text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded hover:bg-surface-3"
                          >
                            Signals
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {selectedScore && (
        <SignalModal score={selectedScore} onClose={() => setSelectedScore(null)} />
      )}
    </div>
  );
}
